import Base2DElement from "./base-2d-element";
import RenderManager from "../rendering/render-manager";
import Utils from "../utils";
import calcScreenspaceBoundingbox from "../utils/calc-screenspace-bbox";
import calcInnerCircle from "../utils/calc-inner-circle";
import Input from "../interactivity/inputs";
import SlideManager from "../utils/slide-manager";
import AssessmentManager from "../assessment/assessment-manager";
import getRelativeCoordinates from "../utils/get-relative-coordinates";

const isDebugVisible = Utils.queryParams.has("debug_plop");
const tmpBox1 = new THREE.Box3(), tmpBox2 = new THREE.Box3();
const tmpVec1 = new THREE.Vector2();
const tmpVec2 = new THREE.Vector2();
const plane = new THREE.Plane();
const line = new THREE.Line3();
const tmp = new THREE.Vector3();
const inverse = new THREE.Matrix4();
const originPlop = new THREE.Vector3();
const raycaster = new THREE.Raycaster();
const tmpMouseEnd = new THREE.Vector2();

const DISPLAY_DOM_ANSWER = false;

class PlopAssessment extends Base2DElement {
    constructor(options, parentLayer, uuid) {
        super(Object.assign({ disableEvents: false }, options), parentLayer, uuid);
    }

    get type2d() { return "PlopAssessment"; }

    get isDropzoneVisible() { return this._visibleDropzone; }
    get isControlsVisible() { return this._visibleControls; }

    setDropzoneVisible(v) {
        this._visibleDropzone = v;

        this._dropzone.classList[v ? "remove" : "add"]("hidden");
    }

    setControlsVisible(v) {
        this._visibleControls = v;

        this._controls.forEach(controls => controls.classList[v ? "remove" : "add"]("hidden"));
    }

    _initValues(options) {
        this._domAnswer = null;
        this._isCorrect = false;
        this._assessment = options.assessment;
        this._visibleDropzone = options.visibleDropzone;
        this._visibleControls = options.visibleControls;
    }

    _createContainer() {
        const container = document.createElementNS(Base2DElement.Namespace, "g");
        container.classList.add("plop-assessment");

        const defs = document.createElementNS(Base2DElement.Namespace, "defs");
        container.appendChild(defs);

        if (isDebugVisible) {
            const g = document.createElementNS(Base2DElement.Namespace, "g");
            const red = document.createElementNS(Base2DElement.Namespace, "g");
            const blue = document.createElementNS(Base2DElement.Namespace, "g");

            this._dbgPath1 = document.createElementNS(Base2DElement.Namespace, "path");
            this._dbgPath2 = document.createElementNS(Base2DElement.Namespace, "path");

            this._dbgCircle1 = document.createElementNS(Base2DElement.Namespace, "circle");
            this._dbgCircle2 = document.createElementNS(Base2DElement.Namespace, "circle");

            red.classList.add("dbg-red");
            blue.classList.add("dbg-blue");

            g.setAttribute("name", "debug-plop");
            g.classList.add("assessment-debug");
            g.appendChild(red);
            g.appendChild(blue);

            red.appendChild(this._dbgPath1);
            red.appendChild(this._dbgCircle1);
            blue.appendChild(this._dbgPath2);
            blue.appendChild(this._dbgCircle2);

            container.appendChild(g);
        }

        const filterUuid = `filter_${this.uuid}`;
        const filter = document.createElementNS(Base2DElement.Namespace, "filter");
        filter.setAttribute("id", filterUuid);
        filter.setAttribute("x", "-50%");
        filter.setAttribute("y", "-50%");
        filter.setAttribute("width", "250%");
        filter.setAttribute("height", "250%");
        defs.appendChild(filter);

        const feOffset = document.createElementNS(Base2DElement.Namespace, "feOffset");
        const feGaussianBlur = document.createElementNS(Base2DElement.Namespace, "feGaussianBlur");
        const feBlend = document.createElementNS(Base2DElement.Namespace, "feBlend");

        feOffset.setAttribute("result", "offOut");
        feOffset.setAttribute("in", "SourceAlpha");
        feOffset.setAttribute("dx", 5);
        feOffset.setAttribute("dy", 5);

        feGaussianBlur.setAttribute("result", "blurOut");
        feGaussianBlur.setAttribute("in", "offOut");
        feGaussianBlur.setAttribute("stdDeviation", 2);

        feBlend.setAttribute("in", "SourceGraphic");
        feBlend.setAttribute("in2", "blurOut");
        feBlend.setAttribute("mode", "normal");

        filter.appendChild(feOffset);
        filter.appendChild(feGaussianBlur);
        filter.appendChild(feBlend);

        const markerUuid = `marker_${this.uuid}`;
        const marker = document.createElementNS(Base2DElement.Namespace, "marker");
        marker.setAttribute("id", markerUuid);
        marker.setAttribute("markerWidth", 10);
        marker.setAttribute("markerHeight", 7);
        marker.setAttribute("refX", 0);
        marker.setAttribute("refY", 1.75);
        marker.setAttribute("orient", "auto");
        defs.appendChild(marker);

        const polygon = document.createElementNS(Base2DElement.Namespace, "polygon");
        polygon.setAttribute("points", "0 0, 5 1.75, 0 3.5");
        polygon.classList.add("marker");
        marker.appendChild(polygon);

        this._line = document.createElementNS(Base2DElement.Namespace, "path");
        this._dropzone = document.createElementNS(Base2DElement.Namespace, "circle");
        this._controls = new Array(2).fill(1).map(_ => document.createElementNS(Base2DElement.Namespace, "circle"));

        this._line.setAttribute("marker-end", `url(#${markerUuid})`);
        this._line.setAttribute("filter", `url(#${filterUuid})`);
        this._line.setAttribute("name", "line");
        this._line.classList.add("line");
        this._dropzone.setAttribute("name", "dropzone");
        this._dropzone.setAttribute("filter", `url(#${filterUuid})`);
        this._dropzone.classList.add("dropzone");

        if (!this._visibleDropzone) this._dropzone.classList.add("hidden");
        container.appendChild(this._dropzone);

        this._controls.forEach(controls => {
            controls.setAttribute("name", "controls");
            controls.classList.add("controls");

            if (!this._visibleControls) controls.classList.add("hidden");
            container.appendChild(controls);
        });

        this._controls[1].classList.add("events");
        this._controls[0].classList.add("visual");


        if (Q3.editMode) {
            container.append(this._line);
            this._onWidgetMouseMoveThis = this._onWidgetMouseMove.bind(this);
            this._onWidgetMouseDownThis = this._onWidgetMouseDown.bind(this)
            this._onWidgetMouseUpThis = this._onWidgetMouseUp.bind(this)

            this._controls[1].addEventListener("mousedown", this._onWidgetMouseDownThis, false);
            this._controls[1].addEventListener("touchstart", this._onWidgetMouseDownThis, false);

            RenderManager.domElement.addEventListener("mouseup", this._onWidgetMouseUpThis, false);
            RenderManager.domElement.addEventListener("touchend", this._onWidgetMouseUpThis, false);
            RenderManager.domElement.addEventListener("mousemove", this._onWidgetMouseMoveThis, false);
            RenderManager.domElement.addEventListener("touchmove", this._onWidgetMouseMoveThis, false);
        } else {
            this._onWidgetMouseDownThis = this._onWidgetViewerMouseDown.bind(this)
            this._controls[1].addEventListener("mousedown", this._onWidgetMouseDownThis, false);
            this._controls[1].addEventListener("touchstart", this._onWidgetMouseDownThis, false);
        }

        this._mouse = {
            isDown: false,
            objectDownOffset: new THREE.Vector3()
        };


        return container;
    }

    _intersectPlane(mouse, out) {
        raycaster.setFromCamera(mouse, RenderManager.activeCamera);

        line.start.copy(raycaster.ray.origin);
        line.end.copy(raycaster.ray.direction)
            .multiplyScalar(RenderManager.activeCamera.far)
            .add(raycaster.ray.origin);

        return plane.intersectLine(line, out);
    }

    disableControls() { this._controls.forEach(controls => controls.classList.add("disabled")); }
    enableControls() { this._controls.forEach(controls => controls.classList.remove("disabled")); }

    /**
     * 
     * @param {PointerEvent} event 
     */
    _onWidgetViewMouseUp(event) {
        this.enableControls();

        document.removeEventListener("mouseup", this._onWidgetMouseUpThis, false);
        document.removeEventListener("touchend", this._onWidgetMouseUpThis, false);

        AssessmentManager.release();

        event.preventDefault();
    }

    /**
     * 
     * @param {PointerEvent} event 
     */
    _onWidgetViewerMouseDown(event) {
        const mouseEnd = getRelativeCoordinates(event, undefined, tmpMouseEnd);
        const mouseScreenspace = RenderManager.toScreenSpaceCoords(mouseEnd);

        Input.mouse.position.copy(mouseScreenspace);
        Input.mouse.updateMouseState();

        if (AssessmentManager.inAssessmentMode)
            AssessmentManager.setLastScreenCoords(mouseScreenspace);

        AssessmentManager.pickup({ object: this._assessment.object }, mouseScreenspace);

        this.disableControls();

        this._onWidgetMouseUpThis = this._onWidgetViewMouseUp.bind(this)
        document.addEventListener("mouseup", this._onWidgetMouseUpThis, false);
        document.addEventListener("touchend", this._onWidgetMouseUpThis, false);

        event.preventDefault();
    }

    /**
     * 
     * @param {PointerEvent} event 
     */
    _onWidgetMouseDown(event) {
        const { object } = this._assessment;

        this._mouse.isDown = true;

        originPlop.setFromMatrixPosition(object.matrixWorld);

        RenderManager.activeCamera.getWorldDirection(plane.normal).negate();
        plane.constant = -originPlop.dot(plane.normal);

        this._mouse.objectDownOffset.set(0, 0, 0);
        this._intersectPlane(Input.mouse.position, this._mouse.objectDownOffset);

        AssessmentManager.pickup({ object: this._assessment.object }, null);

        inverse.getInverse(object.parent.matrixWorld);

        this._mouse.objectDownOffset
            .applyMatrix4(inverse)
            .sub(object.position);

        this._controls.forEach(controls => controls.classList.add("disabled"));
    }

    /**
     * 
     * @param {PointerEvent} event 
     */
    _onWidgetMouseUp(event) {
        if (!this._mouse.isDown) return;

        const { object } = this._assessment;

        Object.assign(
            SlideManager.currentSlide.animation[object.uuid].position,
            {
                x: object.position.x,
                y: object.position.y,
                z: object.position.z
            }
        );

        this._mouse.isDown = false;
        this._controls.forEach(controls => controls.classList.remove("disabled"));
        AssessmentManager.release();

        event.preventDefault();
    }

    /**
     * 
     * @param {PointerEvent} event 
     */
    _onWidgetMouseMove(event) {
        if (!this._mouse.isDown) return;

        const itsct = this._intersectPlane(Input.mouse.position, tmp);

        if (itsct) {
            const { object } = this._assessment;
            inverse.getInverse(object.parent.matrixWorld);

            object.position
                .copy(itsct)
                .applyMatrix4(inverse)
                .sub(this._mouse.objectDownOffset);
        }
    }

    _redrawDebug() {
        const camera = RenderManager.activeCamera;
        const { boundingBox: bbox, plopObject: plop, object, radiusMultiplier: r } = this._assessment;

        tmpBox1.copy(bbox).applyMatrix4(object.matrixWorld);
        tmpBox2.copy(bbox).applyMatrix4(plop.matrixWorld);

        const b1 = calcScreenspaceBoundingbox(tmpBox1, camera);
        const b2 = calcScreenspaceBoundingbox(tmpBox2, camera);

        createOutlinePath(b1, this._dbgPath1);
        createOutlinePath(b2, this._dbgPath2);

        const c1 = calcInnerCircle(b1);
        const c2 = calcInnerCircle(b2);

        this._dbgCircle1.setAttribute("cx", c1.center.x);
        this._dbgCircle1.setAttribute("cy", c1.center.y);
        this._dbgCircle1.setAttribute("r", c1.radius * r);

        this._dbgCircle2.setAttribute("cx", c2.center.x);
        this._dbgCircle2.setAttribute("cy", c2.center.y);
        this._dbgCircle2.setAttribute("r", c2.radius * r);
    }

    redraw() {
        const ass = this._assessment;
        const radius = 24, controlSize = 8;

        if (isDebugVisible) this._redrawDebug();

        const { boundingBox: bbox, plopObject: plop, object } = ass;

        const origObject = RenderManager.toScreenCoords(
            ...bbox
                .getCenter(new THREE.Vector3())
                .applyMatrix4(object.matrixWorld)
                .project(RenderManager.activeCamera)
                .toArray()
                .slice(0, 2)
        );
        const origPlop = RenderManager.toScreenCoords(
            ...bbox
                .getCenter(new THREE.Vector3())
                .applyMatrix4(plop.matrixWorld)
                .project(RenderManager.activeCamera)
                .toArray()
                .slice(0, 2)
        );

        tmpBox1.copy(bbox).applyMatrix4(object.matrixWorld);
        tmpBox2.copy(bbox).applyMatrix4(plop.matrixWorld);

        const len = Math.max(0, origObject.distanceTo(origPlop) - radius - 7 * 4);
        const startPoint = tmpVec1.subVectors(origPlop, origObject).setLength(controlSize + 3).add(origObject);
        const endPoint = tmpVec2.subVectors(origPlop, origObject).setLength(len).add(origObject);

        this._line.setAttribute("d", `M ${startPoint.toArray().join(" ")} L ${endPoint.toArray().join(" ")}`);
        this._dropzone.setAttribute("cx", origPlop.x);
        this._dropzone.setAttribute("cy", origPlop.y);
        this._dropzone.setAttribute("r", radius);

        this._controls.forEach(controls => {
            controls.setAttribute("cx", origObject.x);
            controls.setAttribute("cy", origObject.y);
        });

        if (this._domAnswer) {
            this._domAnswer.setAttribute("x", origObject.x - 50);
            this._domAnswer.setAttribute("y", origObject.y - 25);
        }

        this._controls[1].setAttribute("r", controlSize * (Q3.browserCapabilities.isMobile ? RenderManager.pixelRatio : 1));
        this._controls[0].setAttribute("r", controlSize);
    }

    setCorrectnessState(isCorrect) {
        this._isCorrect = isCorrect;

        if (!this._domAnswer) return;


        if (this._isCorrect) {
            if (this._domAnswer.classList.contains("incorrect")) this._domAnswer.classList.remove("incorrect");
            this._domAnswer.classList.add("correct");
        } else {
            if (this._domAnswer.classList.contains("correct")) this._domAnswer.classList.remove("correct");
            this._domAnswer.classList.add("incorrect");
        }
    }

    setSolved(isSolved) {
        this.setCorrectnessState(isSolved);

        if (!this._domAnswer) {
            this._controls.forEach(control => control.remove());
            this.delete();

            if (DISPLAY_DOM_ANSWER) {
                this._domAnswer = document.createElementNS(Base2DElement.Namespace, "svg");
                this._domAnswer.classList.add("dom-answer", this._isCorrect ? "correct" : "incorrect");

                const background = document.createElementNS(Base2DElement.Namespace, "rect");
                background.classList.add("background");

                background.setAttribute("x", "0");
                background.setAttribute("y", "0");
                background.setAttribute("width", "100");
                background.setAttribute("height", "50");

                const incorrectText = document.createElementNS(Base2DElement.Namespace, "text");
                const correctText = document.createElementNS(Base2DElement.Namespace, "text");

                incorrectText.classList.add("answer-label", "incorrect");
                correctText.classList.add("answer-label", "correct");

                incorrectText.innerHTML = "Incorrect";
                correctText.innerHTML = "Correct";

                [incorrectText, correctText].forEach(text => {
                    text.setAttribute("x", 50);
                    text.setAttribute("y", 25);
                    text.setAttribute("text-anchor", "middle");
                    text.setAttribute("dominant-baseline", "middle");
                });

                this._domAnswer.appendChild(background);
                this._domAnswer.appendChild(correctText);
                this._domAnswer.appendChild(incorrectText);
                this.domElement.appendChild(this._domAnswer);
            }
        }
    }

    delete() {
        this._controls[1].removeEventListener("mousedown", this._onWidgetMouseDownThis, false);
        this._controls[1].removeEventListener("touchstart", this._onWidgetMouseDownThis, false);

        RenderManager.domElement.removeEventListener("mouseup", this._onWidgetMouseUpThis, false);
        RenderManager.domElement.removeEventListener("touchend", this._onWidgetMouseUpThis, false);
        RenderManager.domElement.removeEventListener("mousemove", this._onWidgetMouseMoveThis, false);
        RenderManager.domElement.removeEventListener("touchmove", this._onWidgetMouseMoveThis, false);
    }

    onVisibilityChange(visible) {
        this.domElement.classList[visible ? "remove" : "add"]("hidden");
    }
}

export default PlopAssessment;
export { PlopAssessment };

const createOutlinePath = (function () {
    const o = 5;

    function createX(x, y) {
        return [
            `M ${x - o} ${y - o}`,
            `L ${x + o} ${y + o}`,
            `M ${x + o} ${y - o}`,
            `L ${x - o} ${y + o}`
        ];
    }

    return isDebugVisible
        ? function (box, path) {
            const str = [
                `M ${box.min.x} ${box.min.y}`,
                `L ${box.max.x} ${box.min.y}`,
                `L ${box.max.x} ${box.max.y}`,
                `L ${box.min.x} ${box.max.y}`,
                `L ${box.min.x} ${box.min.y}`,

                createX(box.min.x, box.min.y),
                createX(box.max.x, box.max.y),
                createX(box.min.x, box.max.y),
                createX(box.max.x, box.min.y)
            ].flat().join(" ");
            path.setAttribute("d", str);
        }
        : null;
})();